const PossiblyRequiredFields = [
  "jobEmpPremises",
  "jobComboOccup",
  "jobForeignLanguage",
  "jobReqExceed",
  "empUseCredential",
  "emprecpayment",
  "emPlayoff",
];

const OptionsMap = {
  jobEmpPremises: "G.6",
  jobComboOccup: "G.7",
  jobForeignLanguage: "G.8",
  jobReqExceed: "G.9",
  empUseCredential: "G.10",
  emprecpayment: "G.11",
  emPlayoff: "G.12",
};

export default {
  name: "Appendix C - Supplemental Information",
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description: "Please complete section G - Fields - G.6 - G.12",
        type: "info",
        isVisible: {
          when: (formApi) => {
            let flag = false;
            PossiblyRequiredFields.forEach((field) => {
              if (!formApi.getState().values[field]) {
                flag = true;
              }
            });
            return flag;
          },
          is: "appCSuppInfoReqCheck",
          operator: "customValidation",
        },
      },
      {
        title: "Section Complete",
        description:
          'Because you answered either "No" or "N/A" to all questions G.6 - G.12, this section is not necessary.',
        type: "success",
        isVisible: {
          when: (formApi) => {
            const sectionCompleteCheck = ["No", "N/A"];
            let flag = true;
            PossiblyRequiredFields.forEach((field) => {
              if (!formApi.getState().values[field]) {
                flag = false;
              }
              if (
                !sectionCompleteCheck.includes(formApi.getState().values[field])
              ) {
                flag = false;
              }
            });
            return flag;
          },
          is: "appCSuppCompleteCheck",
          operator: "customValidation",
        },
      },
    ],
  },
  pageInfo: {
    isVisible: {
      when: (formApi) => {
        let flag = false;
        let noUnansweredQuestions = true;
        PossiblyRequiredFields.forEach((field) => {
          if (!formApi.getState().values[field]) {
            noUnansweredQuestions = false;
          }
          if (formApi.getState().values[field] === "Yes") {
            flag = true;
          }
        });
        return flag && noUnansweredQuestions;
      },
      is: "appCSuppPageInfoCheck",
      operator: "customValidation",
    },
    text: `<b><u>Note:</u></b> Employers are required to complete Appendix C when “Yes”
     is marked in any of the questions 6 through 12 under Section G on the Form ETA-9089.
       Specifically, this Appendix is used to elaborate or further explain the business
      necessity of one or more requirements of the employer’s job opportunity under
      consideration for permanent labor certification.<br/><br/>If filing electronically,
      Appendix C will be available in the electronic filing system.  If filing by
      mail, the employer must download and submit the Form ETA-9089, Appendix A,
      and all other required appendices in order for the application to be
      considered complete.<b><i> In accordance with 20 CFR 656.17(a), the Department
      will not certify incomplete applications.</i></b>  Applications and documents
      submitted by mail should include a cover page with the name, title, address, and 
      phone number of the point of contact.  The cover page should also index
      the documents included in the mailing.<br/><br/>This Appendix will collect
      two sections of additional information per page.  For electronic filings,
      if the employer needs to disclose more sections of information, the 
      filing system will automatically provide the employer with the option
      of adding more sections of this Appendix until all required responses
      are completed.  For mailed or paper filings, the employer will make
      one or more copies of this Appendix to complete and attach to the 
      Form ETA-9089.  If the employer is required to undergo supervised 
      recruitment in accordance with 20 CFR 656.21, the Certifying Officer
      may complete this Appendix, on behalf of the employer and as
      applicable. <b><i>Submission of additional information in any other
      form or format will not be accepted.</i></b>  All filers must review
      the Form ETA-9089 for completeness and accuracy prior to submission either by electronic filing or by mail.`,
    coloring: "grey",
  },
  sections: [
    {
      code: "Appendix C",
      machine_name: "appCSupp",
      isModal: true,
      removeFromFieldArray: true,
      openModalHeader: "Add Supplemental Information",
      modalHeader: "Add Supplemental Information",
      sectionHeader: "Supplemental Information",
      listType: "table",
      validate: [
        {
          tableRequiredIf: PossiblyRequiredFields,
          message:
            'One row for each answer marked "Yes" for questions G.6 through G.12',
          needsFieldValues: true,
          extraData: {
            dynamicMinRows: true,
            operator: "||",
            valueOf: "Yes",
          },
        },
      ],
      editButton: true,
      removeLink: "Remove Supplemental Information",
      isVisible: {
        when: (formApi) => {
          let flag = false;
          let noUnansweredQuestions = true;
          PossiblyRequiredFields.forEach((field) => {
            if (!formApi.getState().values[field]) {
              noUnansweredQuestions = false;
            }
            if (formApi.getState().values[field] === "Yes") {
              flag = true;
            }
          });
          return flag && noUnansweredQuestions;
        },
        is: "appCSuppSectionReqCheck",
        operator: "customValidation",
      },
      modalFields: [
        {
          code: "1",
          label: "Section and Item Number",
          machine_name: "sectionAndItemNum",
          widget: "select",
          isInlineModal: true,
          selection: (formApi, counties) => {
            const existingSections =
              formApi
                .getState()
                .values.appCSupp?.map((entry) => entry.sectionAndItemNum) ?? [];

            const requiredOptions = PossiblyRequiredFields.filter(
              (field) => formApi.getState().values[field] === "Yes"
            )
              .map((field) => OptionsMap[field])
              .filter((option) => !existingSections.includes(option));

            const disableOptions = !!requiredOptions.length ? false : true;
            return [disableOptions, requiredOptions];
          },
          dynamicUpdates: [
            {
              set: "sectionName",
              to: "Job Information – Living on Employer’s Premises",
              if: "G.6",
            },
            {
              set: "sectionName",
              to: "Job Information – Combination of Occupations",
              if: "G.7",
            },
            {
              set: "sectionName",
              to: "Job Information – Foreign Language Required",
              if: "G.8",
            },
            {
              set: "sectionName",
              to: "Job Information – Exceed SVP Level",
              if: "G.9",
            },
            {
              set: "sectionName",
              to: "Job Information – Credentialing Service",
              if: "G.10",
            },
            {
              set: "sectionName",
              to: "Job Information – Receipt of Payments",
              if: "G.11",
            },
            {
              set: "sectionName",
              to: "Job Information – Occupational Layoff",
              if: "G.12",
            },
          ],
          validate: ["required"],
          tooltip:
            "Enter the Form ETA-9089 Section and Item number associated with the additional information to be disclosed.  For example, if the employer marked “Yes” that the job opportunity requires the worker to live on the employer’s premises, enter “G.6” in the space provided.  Valid values for this item include the following: G.6, G.7, G.8, G.9, G.10, G.11, and G.12.",
        },
        {
          code: "1.a",
          label: "Section name or category of supplementary information",
          machine_name: "sectionName",
          widget: "select",
          disabled: true,
          selection: [
            "Job Information – Living on Employer’s Premises",
            "Job Information – Combination of Occupations",
            "Job Information – Foreign Language Required",
            "Job Information – Exceed SVP Level",
            "Job Information – Credentialing Service",
            "Job Information – Receipt of Payments",
            "Job Information – Occupational Layoff",
          ],
          validate: ["required"],
          tooltip: `Enter the Form ETA-9089 section name and category associated with the additional information to be disclosed.  For example, if the employer marked “Yes” that the job opportunity requires the worker to live on the employer’s premises, enter “Job Information – Living on Employer’s Premises” in the space provided.  Valid values for this item include the following: 
          <br/><br/>
          For Item G.6 “Job Information – Living on Employer’s Premises”<br/>
          For Item G.7 “Job Information – Combination of Occupations”<br/>
          For Item G.8 “Job Information – Foreign Language Required”<br/>
          For Item G.9 “Job Information – Exceed SVP Level”<br/>
          For Item G.10 “Job Information – Credentialing Service”<br/>
          For Item G.11 “Job Information – Receipt of Payments”<br/>
          For Item G.12 “Job Information – Occupational Layoff”`,
        },
        {
          code: "1.b",
          machine_name: "suppInfo",
          label: "Supplementary Information (up to 1,500 characters)",
          widget: "text_area",
          hideOnTable: true,
          validate: [
            "required",
            {
              minLength: 5,
            },
          ],
          disabled: true,
          disableWhenField: [
            {
              when: "sectionAndItemNum",
              is: "- Select -",
              isInlineModal: true,
            },
            {
              when: "sectionAndItemNum",
              is: null,
              isInlineModal: true,
            },
            {
              when: "sectionAndItemNum",
              is: "",
              isInlineModal: true,
            },
          ],
          minLength: 5,
          inputLimit: 1500,
          tooltip:
            "Enter a brief explanation or business necessity justification in the space provided on the form (up to 1,500 characters).  <b>No attachments will be accepted</b>.",
        },
      ],
    },
  ],
};
